import { offlineDb } from './database';
import { syncEngine } from './sync';
import { supabase } from '@/lib/supabase';
import type { CreateOrderInput } from '@/hooks/useOrders';

// Generate a local order number for offline orders
function generateLocalOrderNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `OFF-${timestamp}${random}`;
}

// Generate a UUID for offline records
function generateLocalId(): string {
  return crypto.randomUUID();
}

export interface OfflineOrder {
  [key: string]: unknown;
  id: string;
  location_id: string;
  user_id: string;
  order_number: string;
  order_type: string;
  customer_id?: string;
  table_id?: string;
  guest_count: number;
  notes?: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  status: string;
  created_at: string;
  is_offline: boolean;
  items: OfflineOrderItem[];
}

export interface OfflineOrderItem {
  id: string;
  order_id: string;
  product_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  modifiers: Array<{ id: string; name: string; price: number }>;
  tax_amount: number;
  total: number;
  notes?: string;
  status: string;
}

const TAX_RATE = 0.15; // 15% GCT

/**
 * Create an order - handles both online and offline scenarios
 */
export async function createOrderOfflineAware(
  input: CreateOrderInput,
  locationId: string,
  userId: string
): Promise<{ order: OfflineOrder; isOffline: boolean; itemsSent: number }> {
  const isOnline = syncEngine.state.isOnline;

  if (isOnline) {
    // Try online creation first
    try {
      return await createOrderOnline(input, locationId, userId);
    } catch (error) {
      console.warn('[Offline] Online order failed, falling back to offline:', error);
      // Fall through to offline creation
    }
  }

  // Create offline order
  return createOrderOffline(input, locationId, userId);
}

/**
 * Create order via Supabase (online mode)
 */
async function createOrderOnline(
  input: CreateOrderInput,
  locationId: string,
  userId: string
): Promise<{ order: OfflineOrder; isOffline: boolean; itemsSent: number }> {
  // Generate order number via RPC
  const { data: orderNumber, error: rpcError } = await supabase.rpc(
    'generate_order_number',
    { p_location_id: locationId }
  );

  if (rpcError) throw rpcError;

  // Calculate totals
  const subtotal = input.items.reduce((sum, item) => {
    const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
    return sum + (item.unit_price + modifierTotal) * item.quantity;
  }, 0);
  const taxAmount = subtotal * TAX_RATE;
  const total = subtotal + taxAmount;

  // Convert order type
  const orderTypeMap: Record<string, string> = {
    'dine-in': 'dine_in',
    'takeout': 'takeout',
    'delivery': 'delivery',
    'dine_in': 'dine_in',
    'bar_tab': 'bar_tab',
  };
  const dbOrderType = orderTypeMap[input.order_type] || 'dine_in';

  // Create order in database
  const { data: order, error: orderError } = await supabase
    .from('orders')
    .insert({
      location_id: locationId,
      user_id: userId,
      order_number: orderNumber,
      order_type: dbOrderType,
      customer_id: input.customer_id,
      guest_count: input.guest_count || 1,
      notes: input.notes,
      subtotal,
      tax_amount: taxAmount,
      total,
      status: 'open',
    })
    .select()
    .single();

  if (orderError) throw orderError;

  // Create order items
  const orderItems = input.items.map(item => {
    const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
    const unitPrice = item.unit_price + modifierTotal;
    const itemTax = unitPrice * item.quantity * TAX_RATE;
    const itemTotal = unitPrice * item.quantity + itemTax;

    return {
      order_id: order.id,
      product_id: item.product_id,
      name: item.name,
      quantity: item.quantity,
      unit_price: unitPrice,
      modifiers: item.modifiers || [],
      tax_amount: itemTax,
      total: itemTotal,
      notes: item.notes,
      status: 'pending',
    };
  });

  const { data: insertedItems, error: itemsError } = await supabase
    .from('order_items')
    .insert(orderItems)
    .select();

  if (itemsError) throw itemsError;

  // Send to kitchen - fetch stations and create kitchen orders
  try {
    // Fetch active kitchen stations for this location
    const { data: stations } = await supabase
      .from('kitchen_stations')
      .select('*')
      .eq('location_id', locationId)
      .eq('is_active', true);

    if (stations && stations.length > 0) {
      // Get product info for routing (station_id, category_id)
      const productIds = input.items.map(item => item.product_id);
      const { data: products } = await supabase
        .from('products')
        .select('id, station_id, category_id, send_to_kitchen')
        .in('id', productIds);

      const productInfo = new Map(
        (products || []).map(p => [p.id, { station_id: p.station_id, category_id: p.category_id, send_to_kitchen: p.send_to_kitchen }])
      );

      // Route items to stations
      const stationOrders = new Map<string, Array<{ id: string; name: string; quantity: number; modifiers: unknown; notes?: string }>>();

      for (let i = 0; i < insertedItems.length; i++) {
        const item = insertedItems[i];
        const info = productInfo.get(input.items[i].product_id);

        // Skip products that have send_to_kitchen disabled
        if (info?.send_to_kitchen === false) continue;

        const productStationId = info?.station_id;
        const categoryId = info?.category_id;

        let targetStation = null;

        // Priority 1: Direct product station assignment
        if (productStationId) {
          targetStation = stations.find(s => s.id === productStationId);
        }

        // Priority 2: Category-based routing
        if (!targetStation && categoryId) {
          targetStation = stations.find(s => {
            if (!s.display_categories || s.display_categories.length === 0) return false;
            return s.display_categories.includes(categoryId);
          });
        }

        // Priority 3: Fallback to "All" station or first active station
        if (!targetStation) {
          targetStation = stations.find(s => s.name.toLowerCase() === 'all') || stations[0];
        }

        if (targetStation) {
          if (!stationOrders.has(targetStation.id)) {
            stationOrders.set(targetStation.id, []);
          }
          stationOrders.get(targetStation.id)!.push({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            modifiers: item.modifiers,
            notes: item.notes,
          });
        }
      }

      // Create kitchen orders for each station
      for (const [stationId, stationItems] of stationOrders) {
        await supabase
          .from('kitchen_orders')
          .insert({
            order_id: order.id,
            station_id: stationId,
            items: stationItems,
            status: 'pending',
            priority: 0,
            sent_at: new Date().toISOString(),
          });
      }

      // Update items status to sent
      await supabase
        .from('order_items')
        .update({ status: 'sent' })
        .in('id', insertedItems.map(i => i.id));

      console.log(`[Order] Created kitchen orders for ${stationOrders.size} station(s)`);
    }
  } catch (kitchenError) {
    // Log but don't fail the order if kitchen order creation fails
    console.error('[Order] Failed to create kitchen orders:', kitchenError);
  }

  // Build response
  const responseOrder: OfflineOrder = {
    id: order.id,
    location_id: order.location_id,
    user_id: order.user_id,
    order_number: order.order_number,
    order_type: order.order_type,
    customer_id: order.customer_id,
    guest_count: order.guest_count,
    notes: order.notes,
    subtotal: order.subtotal,
    tax_amount: order.tax_amount,
    total: order.total,
    status: order.status,
    created_at: order.created_at,
    is_offline: false,
    items: insertedItems.map(item => ({
      id: item.id,
      order_id: item.order_id,
      product_id: item.product_id,
      name: item.name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      modifiers: item.modifiers as Array<{ id: string; name: string; price: number }>,
      tax_amount: item.tax_amount,
      total: item.total,
      notes: item.notes,
      status: item.status,
    })),
  };

  return { order: responseOrder, isOffline: false, itemsSent: insertedItems.length };
}

/**
 * Create order locally (offline mode)
 */
async function createOrderOffline(
  input: CreateOrderInput,
  locationId: string,
  userId: string
): Promise<{ order: OfflineOrder; isOffline: boolean; itemsSent: number }> {
  const orderId = generateLocalId();
  const orderNumber = generateLocalOrderNumber();
  const createdAt = new Date().toISOString();

  // Calculate totals
  const subtotal = input.items.reduce((sum, item) => {
    const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
    return sum + (item.unit_price + modifierTotal) * item.quantity;
  }, 0);
  const taxAmount = subtotal * TAX_RATE;
  const total = subtotal + taxAmount;

  // Convert order type
  const orderTypeMap: Record<string, string> = {
    'dine-in': 'dine_in',
    'takeout': 'takeout',
    'delivery': 'delivery',
    'dine_in': 'dine_in',
    'bar_tab': 'bar_tab',
  };
  const dbOrderType = orderTypeMap[input.order_type] || 'dine_in';

  // Build order items
  const orderItems: OfflineOrderItem[] = input.items.map(item => {
    const modifierTotal = item.modifiers?.reduce((m, mod) => m + mod.price, 0) ?? 0;
    const unitPrice = item.unit_price + modifierTotal;
    const itemTax = unitPrice * item.quantity * TAX_RATE;
    const itemTotal = unitPrice * item.quantity + itemTax;

    return {
      id: generateLocalId(),
      order_id: orderId,
      product_id: item.product_id,
      name: item.name,
      quantity: item.quantity,
      unit_price: unitPrice,
      modifiers: item.modifiers || [],
      tax_amount: itemTax,
      total: itemTotal,
      notes: item.notes,
      status: 'pending',
    };
  });

  // Build the order object
  const order: OfflineOrder = {
    id: orderId,
    location_id: locationId,
    user_id: userId,
    order_number: orderNumber,
    order_type: dbOrderType,
    customer_id: input.customer_id,
    guest_count: input.guest_count || 1,
    notes: input.notes,
    subtotal,
    tax_amount: taxAmount,
    total,
    status: 'open',
    created_at: createdAt,
    is_offline: true,
    items: orderItems,
  };

  // Queue the order for sync
  await offlineDb.queueAction('create', 'orders', {
    id: orderId,
    location_id: locationId,
    user_id: userId,
    order_number: orderNumber,
    order_type: dbOrderType,
    customer_id: input.customer_id || null,
    guest_count: input.guest_count || 1,
    notes: input.notes || null,
    subtotal,
    tax_amount: taxAmount,
    total,
    status: 'open',
    created_at: createdAt,
  });

  // Queue each order item for sync
  for (const item of orderItems) {
    await offlineDb.queueAction('create', 'order_items', {
      id: item.id,
      order_id: item.order_id,
      product_id: item.product_id,
      name: item.name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      modifiers: item.modifiers,
      tax_amount: item.tax_amount,
      total: item.total,
      notes: item.notes || null,
      status: item.status,
    });
  }

  // Also store in local cache for immediate display
  const cachedOrders = await offlineDb.getCachedOrders<OfflineOrder>();
  cachedOrders.unshift(order);
  await offlineDb.cacheOrders(cachedOrders);

  console.log(`[Offline] Created offline order ${orderNumber} with ${orderItems.length} items`);

  return { order, isOffline: true, itemsSent: orderItems.length };
}

/**
 * Get orders - merges cached orders with any pending offline orders
 */
export async function getOrdersOfflineAware(
  locationId: string
): Promise<{ orders: OfflineOrder[]; isFromCache: boolean }> {
  const isOnline = syncEngine.state.isOnline;

  if (isOnline) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          items:order_items(*)
        `)
        .eq('location_id', locationId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      const orders: OfflineOrder[] = (data || []).map(order => ({
        ...order,
        is_offline: false,
        items: order.items || [],
      }));

      // Update cache
      await offlineDb.cacheOrders(orders);

      return { orders, isFromCache: false };
    } catch (error) {
      console.warn('[Offline] Failed to fetch orders online, using cache:', error);
    }
  }

  // Return cached orders
  const cachedOrders = await offlineDb.getCachedOrders<OfflineOrder>();
  return { orders: cachedOrders, isFromCache: true };
}

/**
 * Add payment - handles offline scenario by queuing
 */
export async function addPaymentOfflineAware(
  orderId: string,
  paymentMethod: string,
  amount: number,
  tipAmount?: number,
  paymentTypeId?: string
): Promise<{ success: boolean; isOffline: boolean }> {
  const isOnline = syncEngine.state.isOnline;

  if (isOnline) {
    try {
      const { error } = await supabase
        .from('payments')
        .insert({
          order_id: orderId,
          payment_method: paymentMethod,
          payment_type_id: paymentTypeId,
          amount,
          tip_amount: tipAmount ?? 0,
          status: 'completed',
        });

      if (error) throw error;
      return { success: true, isOffline: false };
    } catch (error) {
      console.warn('[Offline] Payment failed online, queuing:', error);
    }
  }

  // Queue payment for sync
  await offlineDb.queueAction('create', 'payments', {
    id: generateLocalId(),
    order_id: orderId,
    payment_method: paymentMethod,
    payment_type_id: paymentTypeId,
    amount,
    tip_amount: tipAmount ?? 0,
    status: 'completed',
    created_at: new Date().toISOString(),
  });

  return { success: true, isOffline: true };
}

/**
 * Complete order - handles offline scenario by queuing
 */
export async function completeOrderOfflineAware(
  orderId: string
): Promise<{ success: boolean; isOffline: boolean }> {
  const isOnline = syncEngine.state.isOnline;
  const completedAt = new Date().toISOString();

  if (isOnline) {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          status: 'completed',
          completed_at: completedAt,
        })
        .eq('id', orderId);

      if (error) throw error;
      return { success: true, isOffline: false };
    } catch (error) {
      console.warn('[Offline] Complete order failed online, queuing:', error);
    }
  }

  // Queue update for sync
  await offlineDb.queueAction('update', 'orders', {
    id: orderId,
    status: 'completed',
    completed_at: completedAt,
  });

  // Update local cache
  const cachedOrders = await offlineDb.getCachedOrders<OfflineOrder>();
  const orderIndex = cachedOrders.findIndex(o => o.id === orderId);
  if (orderIndex !== -1) {
    cachedOrders[orderIndex].status = 'completed';
    await offlineDb.cacheOrders(cachedOrders);
  }

  return { success: true, isOffline: true };
}
